import React, { useState } from 'react';
import { Input } from 'antd';
import toast from 'react-hot-toast';
import { reportPostApi } from '../../api/posts/report-post.apt';
import { useSelector } from 'react-redux';
import { RootState } from '../../configuration';
import _ from 'lodash';
import DialogComponent from '../dialog/dialog.component';
import { InputWarning } from '../inputwarning/input-warning.component';
import { CustomDropdown } from '../../pages/user-setting/tabs/custom-dropdown';

export enum ReportReason {
  Spam = 0,
  Inappropriate = 1,
  HateSpeech = 2,
  Violence = 3,
  Harassment = 4,
  IntellectualPropertyViolation = 5,
  SuicideOrSelfHarm = 6,
  Misinformation = 7,
  ChildEndangerment = 8,
  SexualContent = 9,
  Other = 10,
}

const maxCountReportReason = {
  [ReportReason.Spam]: 6,
  [ReportReason.Inappropriate]: 10,
  [ReportReason.HateSpeech]: 15,
  [ReportReason.Violence]: 5,
  [ReportReason.Harassment]: 15,
  [ReportReason.IntellectualPropertyViolation]: 15,
  [ReportReason.SuicideOrSelfHarm]: 15,
  [ReportReason.Misinformation]: 6,
  [ReportReason.ChildEndangerment]: 3,
  [ReportReason.SexualContent]: 10,
  [ReportReason.Other]: 15,
};

interface ReportPostDialogProps {
  postId: string;
  onwerPostReportId: string;
  onOpenChange?: (open: boolean) => void; // New prop to notify parent of open state changes
}

export const ReportPostDialog = ({
  postId,
  onwerPostReportId,
  onOpenChange,
}: ReportPostDialogProps) => {
  const [open, setOpen] = useState(false);
  const [reportText, setReportText] = useState('');
  const [reportReason, setReportReason] = useState('');
  const profileUser = useSelector(
    (state: RootState) => state.features.auth.profile
  );
  const profileUserID = _.get(profileUser, 'userId');

  const handleClickOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(true);
    onOpenChange?.(true); // Notify parent that dialog is open
  };

  const handleClose = () => {
    setOpen(false);
    onOpenChange?.(false); // Notify parent that dialog is closed
  };

  const handleReportChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setReportText(event.target.value);
  };

  // Rà soát 28/10: trước đây web tự gọi ẩn bài (lối không tồn tại cho người thường) và báo "thành công" trước khi biết
  // kết quả, kể cả khi đã báo cáo rồi. Ẩn tự động khi đủ ngưỡng do máy chủ làm (user-service phát sự kiện sang post-service).
  const handleSubmit = async () => {
    setOpen(false);
    onOpenChange?.(false); // Notify parent that dialog is closed
    try {
      const result = await reportPostApi({
        postId,
        content: reportText,
        onwerPostReportId,
        reportPostValue: Number(reportReason),
      });
      if (result === 'ALREADY_REPORTED') {
        toast('Bạn đã báo cáo bài viết này rồi.');
        return;
      }
      toast.success('Đã gửi báo cáo. Cảm ơn bạn!');
    } catch {
      toast.error('Chưa gửi được báo cáo, vui lòng thử lại.');
    }
  };

  const reportOptions = [
    { label: 'Spam', value: String(ReportReason.Spam) },
    {
      label: 'Nội dung không phù hợp',
      value: String(ReportReason.Inappropriate),
    },
    { label: 'Lạm dụng', value: String(ReportReason.HateSpeech) },
    { label: 'Bạo lực', value: String(ReportReason.Violence) },
    { label: 'Quấy rối', value: String(ReportReason.Harassment) },
    {
      label: 'Vi phạm sở hữu trí tuệ',
      value: String(ReportReason.IntellectualPropertyViolation),
    },
    {
      label: 'Tự tử hoặc tự làm hại',
      value: String(ReportReason.SuicideOrSelfHarm),
    },
    { label: 'Thông tin sai lệch', value: String(ReportReason.Misinformation) },
    {
      label: 'Nguy hiểm cho trẻ em',
      value: String(ReportReason.ChildEndangerment),
    },
    { label: 'Nội dung tình dục', value: String(ReportReason.SexualContent) },
    { label: 'Khác', value: String(ReportReason.Other) },
  ];

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <button
        onClick={handleClickOpen}
        className="rounded-md flex hover:bg-gray-200 items-center w-full py-2 px-4 gap-2 hover:bg-gray-1 dark:hover:bg-gray-10"
      >
        <svg
          width="25"
          height="24"
          viewBox="0 0 25 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.5 2C6.99 2 2.5 6.49 2.5 12C2.5 17.51 6.99 22 12.5 22C18.01 22 22.5 17.51 22.5 12C22.5 6.49 18.01 2 12.5 2ZM11.75 8C11.75 7.59 12.09 7.25 12.5 7.25C12.91 7.25 13.25 7.59 13.25 8V13C13.25 13.41 12.91 13.75 12.5 13.75C12.09 13.75 11.75 13.41 11.75 13V8ZM13.42 16.38C13.37 16.51 13.3 16.61 13.21 16.71C13.11 16.8 13 16.87 12.88 16.92C12.76 16.97 12.63 17 12.5 17C12.37 17 12.24 16.97 12.12 16.92C12 16.87 11.89 16.8 11.79 16.71C11.7 16.61 11.63 16.51 11.58 16.38C11.53 16.26 11.5 16.13 11.5 16C11.5 15.87 11.53 15.74 11.58 15.62C11.63 15.5 11.7 15.39 11.79 15.29C11.89 15.2 12 15.13 12.12 15.08C12.36 14.98 12.64 14.98 12.88 15.08C13 15.13 13.11 15.2 13.21 15.29C13.3 15.39 13.37 15.5 13.42 15.62C13.47 15.74 13.5 15.87 13.5 16C13.5 16.13 13.47 16.26 13.42 16.38Z"
            fill="#657384"
          />
        </svg>
        <span className="text-base font-normal text-[#18191E]">
          Báo cáo bài viết
        </span>
      </button>
      <DialogComponent
        title="Báo cáo"
        visible={open}
        placement="CENTER"
        size="full"
        customSize="w-full max-w-[976px]"
        onClose={handleClose}
      >
        <div
          className="flex flex-col p-6 gap-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <p className="text-sm text-[#070708]">
                Tại sao bạn báo cáo bài viết này?
              </p>
              <InputWarning />
            </div>
            <CustomDropdown
              value={reportReason}
              onChange={setReportReason}
              options={reportOptions}
              placeholder="Chọn lý do báo cáo"
            />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm text-[#070708]">Nội dung báo cáo :</p>
            <Input.TextArea
              className="text-base"
              rows={4}
              value={reportText}
              onChange={handleReportChange}
              placeholder="Nội dung"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-t-[#DEE0E6] dark:border-t-slate-700 p-6">
          <button
            className="dark:text-slate-50 h-10 px-4 py-2 rounded-lg border border-solid border-[#DEE0E6] dark:border-slate-700 text-base text-[#18191E] cursor-pointer font-medium"
            onClick={handleClose}
          >
            Huỷ bỏ
          </button>
          <button
            className="px-4 py-2 rounded-lg h-10 bg-[#0057FF] text-white text-base font-semibold cursor-pointer"
            onClick={handleSubmit}
          >
            Gửi
          </button>
        </div>
      </DialogComponent>
    </div>
  );
};
